import React, { Fragment, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/utils'

export interface DropdownItem {
  label: string
  onClick?: () => void
  icon?: React.ReactNode
  disabled?: boolean
  danger?: boolean
  divider?: boolean
  shortcut?: string
}

export interface DropdownProps {
  trigger: React.ReactElement
  items: DropdownItem[]
  align?: 'right' | 'left'
  offset?: number
}

export function Dropdown({ trigger, items, align = 'right', offset = 4 }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  const dropdownContent = isOpen ? (
    <Fragment>
      <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} aria-hidden="true" />
      <div
        ref={dropdownRef}
        className={cn(
          'absolute z-50 mt-1.5 min-w-[180px] max-w-[280px] bg-white dark:bg-neutral-900 rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-800',
          'py-1 animate-in fade-in-0 zoom-in-95 duration-150',
          align === 'right' ? 'right-0' : 'left-0'
        )}
        style={{ marginTop: offset }}
        role="menu"
      >
        {items.map((item, index) => {
          if (item.divider) {
            return <hr key={`divider-${index}`} className="h-px bg-neutral-200 dark:bg-neutral-800 my-1 border-0" />
          }
          return (
            <button
              key={index}
              onClick={() => { item.onClick?.(); setIsOpen(false) }}
              disabled={item.disabled}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-sm text-right',
                'text-neutral-700 dark:text-neutral-300',
                'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'focus:outline-none focus:bg-neutral-100 dark:focus:bg-neutral-800',
                item.danger && 'text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20'
              )}
              role="menuitem"
            >
              {item.icon && <span className="w-4 h-4 flex-shrink-0">{item.icon}</span>}
              <span className="flex-1 truncate">{item.label}</span>
              {item.shortcut && (
                <kbd className="text-xs text-neutral-400 dark:text-neutral-500 px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800">
                  {item.shortcut}
                </kbd>
              )}
            </button>
          )
        })}
      </div>
    </Fragment>
  ) : null

  return (
    <>
      <span ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
        {trigger}
      </span>
      {createPortal(dropdownContent, document.body)}
    </>
  )
}